/**
 * Reporting contracts — mirrors Solution.Backend/DTO/Analytics.
 *
 * Row DTOs for all mapped tables and views are generated into `analytics.generated.ts`
 * (`npm run generate:analytics`). This file holds the hand-written query and schema contracts
 * and the scalar aliases the generated DTOs use.
 */

/** SQL `datetime`/`datetime2`, serialized without offset, e.g. `2026-10-01T10:20:30.123`. */
export type SqlDateTime = string;
/** SQL `date`, `yyyy-MM-dd`. */
export type SqlDate = string;
/** SQL `time`, `HH:mm:ss[.fffffff]`. */
export type SqlTime = string;
/** SQL `uniqueidentifier`. */
export type Uuid = string;
/** SQL `binary`/`varbinary`, base64-encoded. */
export type Base64 = string;
/**
 * SQL `bigint`. A number when it fits in a JavaScript number exactly; otherwise the exact digits
 * as a string (see `parseLosslessJson`).
 */
export type JsonInt64 = number | string;
/**
 * SQL `decimal`/`numeric`/`money`. A number when it has at most 15 significant digits;
 * otherwise the exact digits as a string (see `parseLosslessJson`).
 */
export type JsonDecimal = number | string;

/** DTO.Analytics.AnalyticsSchemaColumnDto */
export interface AnalyticsSchemaColumnDto {
  /** Column name as used for `sortBy` and filters (matched case-insensitively). */
  name: string;
  /** SQL base type name, e.g. `nvarchar`, `bigint`, `datetimeoffset`. */
  sqlType: string | null;
  isNullable: boolean;
  /** Storage bytes; -1 = MAX. */
  maxLength: number | null;
  precision: number | null;
  scale: number | null;
}

/** DTO.Analytics.AnalyticsSchemaObjectDto — one item of GET /api/analytics/schema. */
export interface AnalyticsSchemaObjectDto {
  schema: string;
  name: string;
  kind: 'TABLE' | 'VIEW' | (string & {});
  /** Read endpoint, e.g. `/api/analytics/stripe/charges`. */
  path: string;
  defaultSortColumns: string[];
  columns: AnalyticsSchemaColumnDto[];
}

/** Wire names of DTO.Analytics.AnalyticsFilterOperator (API/Infrastructure/AnalyticsQueryParser). */
export const ANALYTICS_FILTER_OPERATORS = [
  'eq',
  'ne',
  'contains',
  'startsWith',
  'gt',
  'gte',
  'lt',
  'lte',
  'isNull',
  'isNotNull',
] as const;

export type AnalyticsFilterOperator = (typeof ANALYTICS_FILTER_OPERATORS)[number];

/** DTO.Analytics.AnalyticsFilter — sent as `filter=Column:operator[:value]`. */
export interface AnalyticsFilter {
  /** Column name from the schema; the backend matches names case-insensitively. */
  column: string;
  operator: AnalyticsFilterOperator;
  /** Omitted for `isNull` / `isNotNull`; an empty string matches empty text. */
  value?: string;
}

export type SortDirection = 'asc' | 'desc';

/** DTO.Analytics.AnalyticsQueryRequest */
export interface AnalyticsQuery {
  pageNumber: number;
  pageSize: number;
  /** Any column of the table; omitted = the backend's catalog ordering. */
  sortBy?: string;
  sortDirection: SortDirection;
  /** Combined with AND. */
  filters: AnalyticsFilter[];
}

/** Limits enforced by DAO/Analytics/AnalyticsQueryBuilder. */
export const ANALYTICS_LIMITS = {
  maxPageSize: 100,
  maxFilters: 20,
  maxFilterValueLength: 4000,
} as const;
